import type { SharedHttpRequest, SharedBatchRequest, ApiError } from '@reqshare/shared';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3000';

export interface GetRequestResult {
  data?: SharedHttpRequest;
  error?: ApiError;
  status: number;
}

export async function getRequest(id: string, token?: string): Promise<GetRequestResult> {
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/requests/${id}`, { headers });

  if (!response.ok) {
    const error: ApiError = {
      statusCode: response.status,
      message: response.statusText,
    };

    try {
      const body = await response.json();
      error.message = body.message || response.statusText;
      error.error = body.error;
    } catch {
      // Use default message from statusText
    }

    return { error, status: response.status };
  }

  const data: SharedHttpRequest = await response.json();
  return { data, status: response.status };
}

export interface GetBatchRequestResult {
  data?: SharedBatchRequest;
  error?: ApiError;
  status: number;
}

export async function getBatchRequest(id: string, token?: string): Promise<GetBatchRequestResult> {
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/requests/batch/${id}`, { headers });

  if (!response.ok) {
    const error: ApiError = {
      statusCode: response.status,
      message: response.statusText,
    };

    try {
      const body = await response.json();
      error.message = body.message || response.statusText;
      error.error = body.error;
    } catch {
      // Use default message from statusText
    }

    return { error, status: response.status };
  }

  const data: SharedBatchRequest = await response.json();
  return { data, status: response.status };
}

export async function updateRequestExpiration(
  id: string,
  expiration: string,
  token: string,
): Promise<{ expiresAt: string | null }> {
  const response = await fetch(`${API_BASE}/requests/${id}/expiration`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ expiration }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update expiration' }));
    throw new Error(error.message);
  }

  return response.json();
}

export async function deleteRequest(id: string, token: string): Promise<void> {
  const response = await fetch(`${API_BASE}/requests/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete request' }));
    throw new Error(error.message);
  }
}

export async function updateBatchExpiration(
  id: string,
  expiration: string,
  token: string,
): Promise<{ expiresAt: string | null }> {
  const response = await fetch(`${API_BASE}/requests/batch/${id}/expiration`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ expiration }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update expiration' }));
    throw new Error(error.message);
  }

  return response.json();
}

export async function deleteBatchRequest(id: string, token: string): Promise<void> {
  const response = await fetch(`${API_BASE}/requests/batch/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete request' }));
    throw new Error(error.message);
  }
}
